/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { ReactElement, useMemo, useRef, useEffect, useState, useCallback } from 'react';

import { getSurveyAPI, updateSurveyAPI } from '@api/controller';
import { B2CResult as B2C_RESULT } from '@constants/b2c';
import { Page } from '@constants/enum';
import { useReduxForm } from '@hooks/useReduxForm';
import { calculatePalgangResult } from '@utils/surveyUtils';
import _ from 'lodash';

import KakaoShareButton from '../../KakaoShareButton';

const Graph = ({
  title,
  leftLabel,
  rightLabel,
  color,
  domain,
  value,
}: {
  title: string;
  leftLabel: string;
  rightLabel: string;
  color: string;
  domain: number[];
  value: number;
}) => {
  const position = _.round(((value - domain[0]) / (domain[1] - domain[0])) * 100);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', rowGap: '5px' }}>
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            style={{
              color: position > 50 ? color : '#AFAFAF',
              fontSize: '15px',
              lineHeight: '1.4em',
              letterSpacing: '-0.6px',
            }}
          >
            {leftLabel}
          </span>
        </div>
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <span
            style={{
              color: '#1D1C1D',
              fontSize: '16px',
              fontWeight: 700,
              lineHeight: '1.3125em',
              letterSpacing: '-0.64px',
            }}
          >
            {title}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            style={{
              color: position < 50 ? color : '#AFAFAF',
              fontSize: '15px',
              lineHeight: '1.4em',
              letterSpacing: '-0.6px',
            }}
          >
            {rightLabel}
          </span>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          position: 'relative',
          width: '100%',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '-3px', width: '55px' }}>
          <span
            style={{
              color: position > 50 ? color : '#AFAFAF',
              fontSize: '15px',
              lineHeight: '1.4em',
              letterSpacing: '-0.4px',
            }}
          >
            {`${position}%`}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            position: 'relative',
            width: '100%',
            height: '14px',
            borderRadius: '30px',
            background:
              position > 50
                ? `linear-gradient(to right, ${color} ${position}%, #e5e5e5 ${position}%)`
                : `linear-gradient(to right, #e5e5e5 ${position}%, ${color} ${position}%)`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: `${position}%`,
              top: '50%',
              borderRadius: '30px',
              width: '18px',
              height: '18px',
              flexShrink: 0,
              background: '#FFFFFF',
              transform: `translate(${-position}%, -50%)`,
              filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25))',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '10px',
                height: '10px',
                flexShrink: 0,
                borderRadius: '50%',
                background: color,
                filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25))',
              }}
            />
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '-3px',
            width: '55px',
            justifyContent: 'flex-end',
          }}
        >
          <span
            style={{
              color: position < 50 ? color : '#AFAFAF',
              fontSize: '15px',
              lineHeight: '1.4em',
              letterSpacing: '-0.4px',
            }}
          >
            {`${100 - position}%`}
          </span>
        </div>
      </div>
    </div>
  );
};

function B2CResult({ setPageIndex }: { setPageIndex: any }): ReactElement {
  const [gd, setGd] = useState<any>();
  const { setFormData } = useReduxForm('b2cResult');
  const { formData: mbtiFlowFormData } = useReduxForm('mbtiFlow');

  const getSurveyData = useCallback(async (id: string) => {
    const res = await (await getSurveyAPI({ id }))?.data?.getSurvey;
    setGd(res ?? {});
  }, []);

  useEffect(() => {
    getSurveyData(localStorage.getItem('survey-id') as string);
  }, []);

  const bData = useMemo(() => JSON.parse(gd?.byeonjeungData ?? '{}'), [gd]);
  const rawData = useMemo(() => JSON.parse(gd?.rawData ?? '{}'), [gd]);
  const bResult = useMemo(() => {
    if (!_.isEmpty(bData)) {
      return calculatePalgangResult({
        ...bData,
        추가응답: {
          표: rawData?.['a-s-pl']?.표 ?? 0,
          리: rawData?.['a-s-pl']?.리 ?? 0,
          한: rawData?.['a-s-hy']?.한 ?? 0,
          열: rawData?.['a-s-hy']?.열 ?? 0,
          허: rawData?.['a-s-hs']?.허 ?? 0,
          실: rawData?.['a-s-hs']?.실 ?? 0,
        },
      });
    }
    return undefined;
  }, [bData]);
  // console.log('bResult', bResult);

  useEffect(() => {
    if (!_.isEmpty(bResult)) {
      setFormData({
        bResult,
      });
    }
  }, [bResult]);

  const scrollAreaRef = useRef(null);

  useEffect(() => {
    const scrollToTop = () => {
      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollIntoView({ block: 'start' });
      }
    };

    // 컴포넌트가 마운트된 후 약간의 지연을 두고 스크롤을 조정합니다.
    const timeoutId = setTimeout(scrollToTop, 0);
  }, []);

  return (
    <div
      ref={scrollAreaRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '14px 15px 20px 15px',
        background: '#C2D5EB',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#FFFFFF',
          width: '100%',
          padding: '35px 20px 20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontSize: '16px',
              color: '#666666',
              filter: _.isEmpty(gd) ? 'blur(4px)' : 'none',
              fontWeight: 700,
              lineHeight: '1.3125em',
              letterSpacing: '-0.64px',
            }}
          >
            {`${localStorage.getItem('survey-name') ?? ''}님의 분석 결과`}
          </span>
        </div>
        <div
          style={{
            marginTop: '5px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontSize: '24px',
              filter: _.isEmpty(gd) ? 'blur(4px)' : 'none',
              fontWeight: 700,
              lineHeight: '1.2em',
              color: '#2B8FFF',
              letterSpacing: '-0.96px',
            }}
          >
            {B2C_RESULT[bResult?.판정결과]?.title}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div
            style={{
              filter: _.isEmpty(gd) ? 'blur(10px)' : 'none',
              marginTop: '15px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '235px',
              height: '235px',
            }}
          >
            <img
              alt={bResult?.판정결과}
              src={`${process.env.PUBLIC_URL}/img/${B2C_RESULT[bResult?.판정결과]?.img}.webp`}
              width={235}
            />
          </div>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '20px',
              rowGap: '16px',
              paddingInline: '10px',
            }}
          >
            <Graph
              color="#80C4FF"
              domain={[0, 100]}
              leftLabel="일시적 (E)"
              rightLabel="만성적 (I)"
              title="병의 깊이"
              value={bResult?.표백분율 ?? 50}
            />
            <Graph
              color="#ECD495"
              domain={[0, 100]}
              leftLabel="차가움 (C)"
              rightLabel="뜨거움 (H)"
              title="열 대사"
              value={bResult?.한백분율 ?? 50}
            />
            <Graph
              color="#78C491"
              domain={[0, 100]}
              leftLabel="부족함 (D)"
              rightLabel="과함 (E)"
              title="기운의 강약"
              value={bResult?.허백분율 ?? 50}
            />
            <Graph
              color="#EB927F"
              domain={[0, 100]}
              leftLabel="음 (Yi)"
              rightLabel="양 (Ya)"
              title="기운의 특성"
              value={bResult?.음백분율 ?? 50}
            />
          </div>
          <div
            style={{
              marginTop: '25px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              borderBottom: '1px solid #E5E5E5',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#2B8FFF33',
                height: '32px',
                width: '100%',
              }}
            >
              <span
                style={{
                  marginBottom: '-2px',
                  color: '#2B8FFF',
                  fontSize: '18px',
                  fontWeight: 700,
                  lineHeight: '1.2em',
                  letterSpacing: '-0.72px',
                }}
              >
                특징
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#D9D9D933',
                width: '100%',
                padding: '10px',
              }}
            >
              <span
                style={{
                  filter: _.isEmpty(gd) ? 'blur(4px)' : 'none',
                  // marginBottom: '-2px',
                  color: '#1D1C1D',
                  fontSize: '16px',
                  lineHeight: '1.4375em',
                  letterSpacing: '-0.64px',
                }}
              >
                {B2C_RESULT[bResult?.판정결과]?.desc}
              </span>
            </div>
          </div>
          <div
            style={{
              marginTop: '10px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#2B8FFF33',
                height: '32px',
                width: '100%',
              }}
            >
              <span
                style={{
                  marginBottom: '-2px',
                  color: '#2B8FFF',
                  fontSize: '18px',
                  fontWeight: 700,
                  lineHeight: '1.2em',
                  letterSpacing: '-0.72px',
                }}
              >
                생활 Tip
              </span>
            </div>
            {B2C_RESULT[bResult?.판정결과]?.tip?.map((tip, idx) => (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#D9D9D933',
                  width: '100%',
                  padding: '10px',
                  borderBottom: '1px solid #E5E5E5',
                }}
              >
                <span
                  style={{
                    filter: _.isEmpty(gd) ? 'blur(4px)' : 'none',
                    color: '#1D1C1D',
                    fontSize: '16px',
                    lineHeight: '1.4375em',
                    letterSpacing: '-0.64px',
                  }}
                >
                  <span
                    style={{
                      color: '#2B8FFF',
                      fontSize: '16px',
                      fontWeight: 700,
                      lineHeight: '1.4375em',
                      letterSpacing: '-0.64px',
                    }}
                  >{`Tip ${idx + 1}. `}</span>
                  {tip}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        style={{
          marginTop: '25px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          rowGap: '8px',
        }}
      >
        <button
          onClick={() => {
            updateSurveyAPI({
              id: gd.id,
              solutionClickCount: (gd.solutionClickCount || 0) + 1,
              _version: gd._version,
            });
            setPageIndex(Page.B2CSolution);
          }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#2B8FFF',
            width: 'calc(100% - 32px)',
            height: '52px',
            borderRadius: '20px',
            border: 'none',
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: 700,
            letterSpacing: '0.1px',
          }}
          type="button"
        >
          내 몸에 맞는 약재 찾기
        </button>
        {!mbtiFlowFormData?.uuid && <KakaoShareButton />}
        {!mbtiFlowFormData?.uuid && (
          <button
            onClick={() => {
              setPageIndex(Page.B2CMain);
            }}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: '#FFFFFF',
              width: 'calc(100% - 32px)',
              height: '52px',
              borderRadius: '20px',
              border: 'none',
              color: '#1D1C1D',
              fontSize: '20px',
              fontWeight: 700,
              letterSpacing: '0.1px',
            }}
            type="button"
          >
            다시 분석하기
          </button>
        )}
      </div>
    </div>
  );
}

export default B2CResult;
